import { z } from 'zod';

import { GatheringStatusSchema, RsvpSchema, TournamentFormatSchema } from '../enums';
import { IdSchema } from './common';
import { MemberPublicSchema } from './member';

export const RsvpInputSchema = z.object({
  gatheringId: IdSchema,
  rsvp: RsvpSchema,
});
export type RsvpInput = z.infer<typeof RsvpInputSchema>;

/** Ответ за участника от капитана — после T-24h участник сам менять ответ не может (Stage 6). */
export const RsvpForMemberInputSchema = RsvpInputSchema.extend({ userId: IdSchema });
export type RsvpForMemberInput = z.infer<typeof RsvpForMemberInputSchema>;

export const GeoCheckinInputSchema = z.object({
  gatheringId: IdSchema,
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type GeoCheckinInput = z.infer<typeof GeoCheckinInputSchema>;

export const ActivityRefSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
});
export type ActivityRef = z.infer<typeof ActivityRefSchema>;

export const VenuePublicSchema = z.object({
  id: IdSchema,
  name: z.string(),
  address: z.string(),
  lat: z.number(),
  lng: z.number(),
});
export type VenuePublic = z.infer<typeof VenuePublicSchema>;

/** Отряд-соперник на турнире (Stage 12, задача 3): общая карточка сбора у двух отрядов. */
export const OpponentSquadSchema = z.object({
  squadId: IdSchema,
  name: z.string().nullable(),
  emblem: z.string().nullable(),
  levelBand: z.number().int().min(1).max(5),
  /** Сбор-близнец соперника — результат зеркалится в него */
  gatheringId: IdSchema.nullable(),
});
export type OpponentSquad = z.infer<typeof OpponentSquadSchema>;

/** Счёт турнира, вносит капитан (Stage 12, задача 3). */
export const GatheringResultSchema = z.object({
  ours: z.number().int().min(0).max(999),
  theirs: z.number().int().min(0).max(999),
  note: z.string().trim().max(200).nullable().default(null),
});
export type GatheringResult = z.infer<typeof GatheringResultSchema>;

export const GatheringResultInputSchema = GatheringResultSchema.extend({ gatheringId: IdSchema });
export type GatheringResultInput = z.infer<typeof GatheringResultInputSchema>;

/** Публичный DTO сбора (BLUEPRINT 6): без служебных полей, время — ISO UTC. */
export const GatheringPublicSchema = z.object({
  id: IdSchema,
  seasonId: IdSchema,
  squadId: IdSchema,
  index: z.number().int().min(1),
  activity: ActivityRefSchema,
  venue: VenuePublicSchema.nullable(),
  startsAt: z.string().datetime(),
  durationMin: z.number().int().positive(),
  /** Таймзона города отряда — клиент показывает дату и время в ней */
  timezone: z.string(),
  status: GatheringStatusSchema,
  minAttendance: z.number().int().positive(),
  /** «Что взять»: без строки про оплату доп. сбора — она в `paysOnSite` (раунд персон 1, C4) */
  notes: z.string().nullable(),
  isExtra: z.boolean(),
  /**
   * Доп. сбор (опрос отряда Stage 12 или сбор ops) вне цены сезона: составу — оплата на месте.
   * Гостю (разовое место, замена, пробный) — false: своё место он оплатил заявкой (раунд персон 1, C4).
   */
  paysOnSite: z.boolean(),
  opponentSquadId: IdSchema.nullable(),
  /** Соперник на турнире (Stage 12, задача 3) */
  opponent: OpponentSquadSchema.nullable(),
  /** Счёт турнира: заполняет капитан после сбора */
  result: GatheringResultSchema.nullable(),
  /**
   * Stage 17: формат турнира (`tournamentFormatOf`: пусто у турнира = `STANDARD`), null — не турнир.
   * Строку формата клиент показывает только с `feature.myGatherings`
   */
  tournamentFormat: TournamentFormatSchema.nullable().default(null),
  /** Сбор перенесён: у прежней даты с тем же `index` стоит CANCELLED (Stage 5) */
  rescheduled: z.boolean(),
  cancelledReason: z.string().nullable(),
  /** После этого момента участник не меняет ответ сам — только через капитана (T-24h) */
  rsvpLockAt: z.string().datetime(),
  myRsvp: RsvpSchema.nullable(),
  /** Отметка явки уже стоит (капитан или гео-чек-ин, Stage 9) */
  myCheckedIn: z.boolean(),
  rsvpCounts: z.object({
    yes: z.number().int().nonnegative(),
    no: z.number().int().nonnegative(),
    maybe: z.number().int().nonnegative(),
  }),
});
export type GatheringPublic = z.infer<typeof GatheringPublicSchema>;

/** Состав на сбор: активные участники с их ответами (Stage 6, задача 4). */
export const GatheringRosterEntrySchema = MemberPublicSchema.extend({
  rsvp: RsvpSchema.nullable(),
  /**
   * Сезон оплачен (по `Order`) — только капитану, остальным null (раунд 5, M3): по отсутствию
   * метки «оплачен» должник был виден всему составу, а политика обещает это только капитану.
   */
  paid: z.boolean().nullable(),
  /**
   * Основной участник без оплаты сезона — только капитану (раунд 4, C5); остальным null.
   * Запасного не помечаем: его доля платится отдельно и не входит в минимум оплат.
   */
  unpaid: z.boolean().nullable(),
});
export type GatheringRosterEntry = z.infer<typeof GatheringRosterEntrySchema>;

export const GatheringRosterSchema = z.object({
  gatheringId: IdSchema,
  members: z.array(GatheringRosterEntrySchema),
  /** Текущий пользователь — капитан отряда: может отвечать за участников */
  canManage: z.boolean(),
  /**
   * Открытые места отряда на этот сбор — только капитану (раунд 3, C4): места на сезон и
   * запасного плюс замены на этот сбор. `offered` — из них уже предложены кандидату.
   * Людей не раскрывает: кому предложено, капитан не видит. Участнику — null.
   */
  openSeats: z
    .object({ total: z.number().int().nonnegative(), offered: z.number().int().nonnegative() })
    .nullable(),
  /**
   * Раунд персон 3 (M3): сколько на сборе мест не из состава (разовое место, замена, пробный).
   * Гость не видел на чужом сборе ни себя, ни своего места: состав отряда и счётчик ответов его
   * не считают. Имена гостей по-прежнему только капитану (`captain.roster`)
   */
  guestsCount: z.number().int().nonnegative().default(0),
});
export type GatheringRoster = z.infer<typeof GatheringRosterSchema>;
